import { apiFetch, readErrorMessage } from '../api/client';
import { editorHrefForKind } from '../editorShell/editorRoutes';
import { surfaceToEditorTabKind } from '../editorShell/editorWorkspaceIds';
import { USER_STORAGE_MOUNT_ID } from '../storageFileAudit/formatters';
import {
  joinContentFolderPath,
  readDefaultContentFolderPath
} from './defaultContentFolderPrefs';
import { ensureUserStorageFolder } from './ensureUserStorageFolder';
import { mountApiBase } from './fileBrowserApi';
import { normalizeStarterFileName } from './fileBrowserNewFile';
import { getNewFileStarter, type NewFileStarter } from './fileTypeRegistry';

const MAX_NAME_ATTEMPTS = 50;

/** My Apps launchers for Workspace text / markdown / diagram / code surfaces. */
export type MyAppsWorkspaceStarterId =
  | 'txt'
  | 'jenc'
  | 'log'
  | 'env'
  | 'md'
  | 'drawio'
  | 'json'
  | 'jsonc'
  | 'xml'
  | 'yaml'
  | 'js'
  | 'ts'
  | 'tsx'
  | 'py'
  | 'ps1'
  | 'cs'
  | 'sql'
  | 'sh'
  | 'html'
  | 'css'
  | 'ini';

export type CreatedBlankStarterFile = {
  mountId: string;
  relativePath: string;
  fileName: string;
  openSurface: Extract<NewFileStarter['openSurface'], 'monaco' | 'code' | 'markdown' | 'drawio'>;
  editorHref: string;
};

function assertWorkspaceStarter(starterId: MyAppsWorkspaceStarterId): NewFileStarter {
  const starter = getNewFileStarter(starterId);
  if (!starter || starter.stub) {
    throw new Error('Unknown file type.');
  }
  if (
    starter.openSurface !== 'monaco' &&
    starter.openSurface !== 'code' &&
    starter.openSurface !== 'markdown' &&
    starter.openSurface !== 'drawio'
  ) {
    throw new Error('Starter is not a Workspace text/code/markdown/diagram editor type.');
  }
  return starter;
}

function numberedFileName(fileName: string, extension: string, attempt: number): string {
  if (attempt <= 1) return fileName;
  const base = fileName.slice(0, -extension.length);
  return `${base} (${attempt})${extension}`;
}

/** Builds Notes.txt / data.json / …, then Notes (2).txt, … */
export function nextStarterFileName(
  starterId: MyAppsWorkspaceStarterId,
  attempt: number,
  preferredFileName?: string
): string {
  const starter = assertWorkspaceStarter(starterId);
  const fileName = preferredFileName
    ? normalizeStarterFileName(preferredFileName, starter)
    : starter.defaultName;
  return numberedFileName(fileName, starter.extension, attempt);
}

function blankStarterFile(starter: NewFileStarter, fileName: string): File {
  return new File([starter.blankContent], fileName, { type: starter.mimeType });
}

/**
 * Creates a blank text/markdown/diagram/code file in My Storage default content folder
 * and returns metadata for openEditorBySurface / Workspace launch.
 */
export async function createBlankStarterInUserStorage(
  starterId: MyAppsWorkspaceStarterId,
  mountId: string = USER_STORAGE_MOUNT_ID,
  preferredFileName?: string,
  folderPath?: string
): Promise<CreatedBlankStarterFile> {
  const starter = assertWorkspaceStarter(starterId);
  const openSurface = starter.openSurface as CreatedBlankStarterFile['openSurface'];
  const tabKind = surfaceToEditorTabKind(openSurface);
  if (!tabKind) throw new Error('Unsupported editor surface.');

  const folder = await ensureUserStorageFolder(
    folderPath ?? readDefaultContentFolderPath(),
    mountId
  );
  let lastError = 'Could not create file.';

  for (let attempt = 1; attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    const fileName = nextStarterFileName(starterId, attempt, preferredFileName);
    const file = blankStarterFile(starter, fileName);
    const form = new FormData();
    form.append('file', file);
    const uploadPath = folder ? encodeURIComponent(folder) : '';
    const res = await apiFetch(`${mountApiBase(mountId)}/upload?path=${uploadPath}`, {
      method: 'POST',
      body: form
    });

    if (res.ok) {
      const relativePath = joinContentFolderPath(folder, fileName);
      return {
        mountId,
        relativePath,
        fileName,
        openSurface,
        editorHref: editorHrefForKind(tabKind, mountId, relativePath)
      };
    }

    if (res.status === 409) {
      lastError = `A file named "${fileName}" already exists.`;
      continue;
    }

    throw new Error(await readErrorMessage(res));
  }

  throw new Error(lastError);
}
